import { computed, onBeforeUnmount, toValue, watch } from "vue";
import { useEventListener, useResizeObserver } from "@vueuse/core";
import { arrow, autoUpdate, detectOverflow, flip, offset, shift, size, useFloating } from "@floating-ui/vue";
import { resetResolvedLengths, resolveLengthToPx } from "#ce/utils/css";
import { toBoolean } from "#ce/utils/props";

// Map the legacy `location` / `horizontalAlign` props onto a Floating UI
// placement. A bare side (`top`/`bottom`) centres the popup on that side; only
// the `-start` / `-end` variants align to the trigger's start/end edge.
export function toPlacement({ location, horizontalAlign } = {}) {
  if (location === "top-left") {
    return "top-start";
  }
  if (location === "top-right") {
    return "top-end";
  }
  if (location === "top") {
    return "top";
  }
  if (location === "bottom-left") {
    return "bottom-start";
  }
  if (location === "bottom-right") {
    return "bottom-end";
  }
  if (horizontalAlign === "left") {
    return "bottom-start";
  }
  if (horizontalAlign === "right") {
    return "bottom-end";
  }
  return "bottom";
}

// Below this much available space the popup flips to the other side instead of
// squeezing further, unless that side has less room still. Keeps the preferred
// (bottom) placement whenever a usable amount of the list fits, matching the old
// in-DOM popup's open-downward bias.
const MIN_POPUP_HEIGHT = 220;

// Default gap between the activator and the popup (px). Tight by default so the
// app-wide selects stay snug; consumers that want a roomier gap (the doctor
// finder search/filters) set `--sp-comp-popup-offset` on a light-DOM ancestor —
// it inherits into this shadow tree and is read per-update below.
const DEFAULT_OFFSET = 4;
const DEFAULT_POINTER_OFFSET = 10;

const FLIP_PROPERTY = "--sp-comp-popup-flip";

export const ROUND_COORDINATES_PROPERTY = "--sp-comp-popup-round-coordinates";

// Matches the viewport padding the size() and flip() middleware keep.
export const VIEWPORT_PADDING = 8;

/**
 * Whether the popup may move to the opposite side when its own side runs out of room. Hosts opt out
 * with the `no-flip` prop or `--sp-comp-popup-flip: false` on a light-DOM ancestor, which inherits into this shadow tree.
 * A locked popup still caps to `MIN_POPUP_HEIGHT` and overflows the viewport past that.
 * `useLockedFocusReveal` scrolls the page to keep its keyboard-focused control on screen.
 *
 * @param {HTMLElement} floating The floating element.
 * @returns {boolean}
 */
export function allowsFlip(floating) {
  return getComputedStyle(floating).getPropertyValue(FLIP_PROPERTY).trim().toLowerCase() !== "false";
}

/**
 * Returns the larger of the room above and below the anchor, less the offset between anchor and
 * popup. Measured against the clipping boundary and `VIEWPORT_PADDING` that Floating UI's
 * `availableHeight` uses.
 *
 * @param {import("@floating-ui/vue").MiddlewareState} state
 * @returns {Promise<number>}
 */
async function roomOnRoomierSide(state) {
  const anchorOverflow = await detectOverflow(state, {
    elementContext: "reference",
    altBoundary: true,
    padding: VIEWPORT_PADDING,
  });
  // `offset()` records its main-axis shift as a y delta: positive moves a bottom popup away from the
  // anchor, negative moves a top one away. A negative gap is an overlap, which adds to the room.
  const shift = state.middlewareData.offset?.y ?? 0;
  const gap = state.placement.startsWith("top") ? -shift : shift;

  return Math.max(-anchorOverflow.top, -anchorOverflow.bottom) - gap;
}

/**
 * Floating UI positioning for the popup: places and sizes it against its anchor.
 * Separate from the visibility path — Floating UI writes inline styles, so it
 * runs the same whether the popup is shown via the native Popover API or the
 * no-API fallback.
 *
 * @param {object} options
 * @param {import("vue").Ref<HTMLElement|null>} options.popup Floating element ref.
 * @param {import("vue").Ref<HTMLElement|null>} options.arrowEl Arrow element ref.
 * @param {() => HTMLElement | { getBoundingClientRect: () => DOMRect, contextElement?: Element } | null} options.anchor
 *   Anchor getter — resolves the reference (HTML or virtual) element, or null.
 * @param {() => string} options.location `location` prop getter.
 * @param {() => string} options.horizontalAlign `horizontalAlign` prop getter.
 * @param {() => boolean} options.noFlip Whether the popup must keep its requested placement.
 * @param {() => boolean} options.hasPointer Whether the arrow pointer is rendered.
 * @param {() => boolean} options.enabled Floating (popover) variant — false for permanent.
 * @param {import("vue").Ref<boolean>} options.isActive Open state — Floating UI's `open` input.
 * @returns {{ popupStyle: import("vue").ComputedRef, arrowStyle: import("vue").ComputedRef, isLocationTop: import("vue").ComputedRef, startAutoUpdate: () => void, stopAutoUpdate: () => void }}
 */
export function usePopupPlacement({
  popup,
  arrowEl,
  anchor,
  location,
  horizontalAlign,
  noFlip = () => false,
  hasPointer,
  enabled,
  isActive,
}) {
  const anchorRef = computed(() => toValue(anchor) ?? null);

  const placement = computed(() =>
    toPlacement({ location: toValue(location), horizontalAlign: toValue(horizontalAlign) }),
  );

  /**
   * Floating UI positioning pipeline (offset → size → flip → shift → arrow) that
   * places and sizes the popup against its anchor. Separate from the visibility
   * path: Floating UI writes inline styles, so it runs the same whether the popup
   * is shown via the native Popover API or the no-API fallback.
   */
  const middleware = computed(() => {
    const list = [
      offset(({ elements }) => {
        const style = getComputedStyle(elements.floating);
        const rawMain = style.getPropertyValue("--sp-comp-popup-offset");
        const rawInline = style.getPropertyValue("--sp-comp-popup-offset-inline");
        const mainAxis =
          resolveLengthToPx(rawMain, elements.floating, rawInline) ??
          (toValue(hasPointer) ? DEFAULT_POINTER_OFFSET : DEFAULT_OFFSET);
        // `--sp-comp-popup-offset-inline` shifts the popup along its anchor's edge, towards the inline
        // end: Floating UI reverses the cross axis of a top or bottom placement in a right-to-left layout.
        const crossAxis = resolveLengthToPx(rawInline, elements.floating) ?? 0;

        return { mainAxis, crossAxis };
      }),
      // size() runs BEFORE flip() on purpose: it caps the popup to the space on
      // the preferred side (with a floor), so flip() measures the capped element
      // and only flips when even MIN_POPUP_HEIGHT doesn't fit. With flip() first,
      // its bestFit fallback compares the *uncapped* content height against both
      // sides and effectively coin-flips tall lists to the top.
      size({
        padding: 8,
        async apply(state) {
          const { availableHeight, rects, elements } = state;

          if (!toValue(enabled)) {
            return;
          }
          // `--sp-comp-popup-width/min-width: 100%` meant "as wide as the trigger"
          // under the old in-DOM positioning. In the top layer 100% resolves to the
          // viewport, so translate those (and only those) to the anchor's width.
          const refWidth = `${Math.round(rects.reference.width)}px`;
          const computed = getComputedStyle(elements.floating);
          if (rects.reference.width && computed.getPropertyValue("--sp-comp-popup-width").trim() === "100%") {
            elements.floating.style.setProperty("width", refWidth, "important");
            // max-width beats width regardless of importance, so the stylesheet's
            // 60ch default cap would clamp anchors wider than 60ch of the font.
            elements.floating.style.setProperty("max-width", refWidth, "important");
          }
          if (rects.reference.width && computed.getPropertyValue("--sp-comp-popup-min-width").trim() === "100%") {
            elements.floating.style.setProperty("min-width", refWidth, "important");
          }
          // Cap height to the space Floating UI found and let the content scroll,
          // instead of overflowing off-screen. The cap is the content box's: the
          // popup's block borders and its content's block padding come off it.
          // floor() — never round up: a cap a sub-pixel above the true available
          // space reads as overflow to flip() and sends the popup to the wrong side.
          const contentStyle = getComputedStyle(elements.floating.querySelector(".sp-popup__content"));
          const chrome =
            parseFloat(computed.borderTopWidth) +
            parseFloat(computed.borderBottomWidth) +
            parseFloat(contentStyle.paddingTop) +
            parseFloat(contentStyle.paddingBottom);
          // A popup that may flip takes no more than the roomier side offers, so it
          // fits a window short of `MIN_POPUP_HEIGHT` on both sides. A locked popup
          // keeps the full `MIN_POPUP_HEIGHT`.
          const floor = allowsFlip(elements.floating)
            ? Math.min(MIN_POPUP_HEIGHT, (await roomOnRoomierSide(state)) - chrome)
            : MIN_POPUP_HEIGHT;

          elements.floating.style.setProperty(
            "--sp-popup-available-height",
            `${Math.max(0, Math.floor(Math.max(floor, availableHeight - chrome)))}px`,
          );
        },
      }),
      // Turning both axis checks off leaves flip() with no overflow to react to, so the popup keeps
      // the placement it was given. Read per update off the floating element, which is what carries
      // the token down from the host.
      flip((state) => {
        const locked = !allowsFlip(state.elements.floating);

        return { padding: 8, mainAxis: !locked, crossAxis: !locked };
      }),
      shift({ padding: 8 }),
    ];

    if (toValue(hasPointer)) {
      list.push(arrow({ element: arrowEl, padding: 12 }));
    }

    return list;
  });

  const {
    floatingStyles,
    x,
    y,
    middlewareData,
    placement: resolvedPlacement,
    update,
    isPositioned,
  } = useFloating(anchorRef, popup, {
    strategy: "fixed",
    placement,
    middleware,
    open: isActive,
  });

  function updatePlacementForFlipChange() {
    update();
  }

  watch(noFlip, updatePlacementForFlipChange, { flush: "post" });

  // SpPopup keeps the floating element mounted and toggles visibility via the
  // Popover API / CSS `display`, so `whileElementsMounted: autoUpdate` would keep
  // scroll/resize/resizeObserver listeners running on every mounted popup even
  // while closed. Drive autoUpdate manually instead — started on show, stopped on
  // hide — so listeners exist only while the popup is visible.
  let cleanupAutoUpdate = null;

  const activeFloating = computed(() => (toValue(isActive) && toValue(enabled) ? popup.value : null));
  const activeReference = computed(() => {
    const reference = toValue(anchorRef);
    if (!activeFloating.value) {
      return null;
    }
    return reference instanceof Element ? reference : (reference?.contextElement ?? null);
  });

  function updateAfterResize() {
    if (!activeFloating.value) {
      return;
    }
    resetResolvedLengths(activeFloating.value);
    update();
  }

  useResizeObserver([activeFloating, activeReference], updateAfterResize);
  useEventListener(() => activeFloating.value?.ownerDocument.defaultView, "resize", updateAfterResize);
  useEventListener(() => activeFloating.value?.ownerDocument.defaultView?.visualViewport, "resize", updateAfterResize);

  function startAutoUpdate() {
    if (!toValue(enabled)) {
      return;
    }
    const referenceEl = toValue(anchorRef);
    const floatingEl = popup.value;
    if (!referenceEl || !floatingEl) {
      return;
    }
    stopAutoUpdate();
    resetResolvedLengths(floatingEl);
    // Resizes go through updateAfterResize, which clears cached expression lengths before repositioning.
    cleanupAutoUpdate = autoUpdate(referenceEl, floatingEl, update, { ancestorResize: false, elementResize: false });
    update();
  }

  function stopAutoUpdate() {
    if (cleanupAutoUpdate) {
      cleanupAutoUpdate();
      cleanupAutoUpdate = null;
    }
  }

  onBeforeUnmount(stopAutoUpdate);

  // Only the floating (popover) variant takes Floating UI's inline position;
  // permanent stays in normal flow.
  const popupStyle = computed(function positionedStyle() {
    if (!toValue(enabled)) {
      return {};
    }

    const style = {
      ...floatingStyles.value,
      [FLIP_PROPERTY]: toBoolean(toValue(noFlip)) ? "false" : undefined,
    };
    const floating = popup.value;

    if (floating && getComputedStyle(floating).getPropertyValue(ROUND_COORDINATES_PROPERTY).trim() === "false") {
      style.transform = `translate(${x.value}px, ${y.value}px)`;
    }

    return style;
  });

  const isLocationTop = computed(() => resolvedPlacement.value?.startsWith("top"));

  // Position the optional arrow against the anchor (tracks flip/shift).
  const arrowStyle = computed(() => {
    const data = middlewareData.value.arrow;
    if (!data) {
      return {};
    }
    const style = {};
    if (data.x != null) {
      style.left = `${data.x}px`;
    }
    if (data.y != null) {
      style.top = `${data.y}px`;
    }
    return style;
  });

  return { popupStyle, arrowStyle, isLocationTop, isPositioned, startAutoUpdate, stopAutoUpdate };
}
